import axios from 'axios';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import packageJson from '../../../package.json';
import { deleteCaches, getLocalStorage, setLocalStorage } from '../../common/common';
import pako from 'pako';
import { safeJsonParser } from '../../utils/common';

const baseURL = process.env.REACT_APP_API_URL;
const authService = process.env.REACT_APP_API_URL;
const apiVersion = packageJson.version;

let accessToken = '';
accessToken = getLocalStorage('accessToken', '') ? getLocalStorage('accessToken', '') : null;

// Object to track disabled APIs
const disabledApis = {};

// Queue to store pending requests during token refresh
let isRefreshing = false;
let refreshSubscribers = [];

const axiosInstance = axios.create({
  baseURL,
  headers: {
    Authorization: getLocalStorage('accessToken', ''),
    version: `rjsw ${apiVersion}`
  }
});

const clearLocalStorage = () => {
  deleteCaches();
  const allKeys = Object.keys(localStorage);

  for (const key of allKeys) {
    if (!key.includes('consentAccepted')) {
      localStorage.removeItem(key);
    }
  }
  sessionStorage.clear();
};

// Function to process the queue of pending requests
const processQueue = (error, token = null) => {
  refreshSubscribers.forEach((callback) => {
    if (error) {
      callback(error);
    } else {
      callback(token);
    }
  });

  refreshSubscribers = [];
};

// Function to add request to queue
const addToQueue = (request) => {
  return new Promise((resolve, reject) => {
    refreshSubscribers.push((token) => {
      if (token) {
        request.headers['Authorization'] = token;
        resolve(axiosInstance(request));
      } else {
        reject(new Error('Failed to refresh token'));
      }
    });
  });
};

const obtainNewAccessToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshSubscribers.push((token) => {
        if (token) {
          resolve(token);
        } else {
          reject(new Error('Failed to refresh token'));
        }
      });
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(`${authService}auth/getAccessToken`, {
      token: getLocalStorage('refreshToken', '')
    });

    if (response.data.status_code === 200) {
      const newToken = response.data.data.token;
      setLocalStorage('accessToken', newToken);
      processQueue(null, newToken);
      isRefreshing = false;
      return newToken;
    }

    if (response.data.status_code === 401) {
      try {
        const refreshResponse = await axios.post(`${authService}auth/getRefreshToken`, {
          token: getLocalStorage('refreshToken', '')
        });

        if (refreshResponse.data.status_code === 200) {
          const newToken = refreshResponse.data.data.token;
          setLocalStorage('accessToken', newToken);
          setLocalStorage('refreshToken', refreshResponse.data.data.refresh_token);
          processQueue(null, newToken);
          isRefreshing = false;
          return newToken;
        }

        throw new Error('Refresh token failed');
      } catch (error) {
        processQueue(error);
        isRefreshing = false;
        // clearLocalStorage();
        // window.location.href = '/';
        return Promise.reject('loginAgain');
      }
    }
  } catch (error) {
    processQueue(error);
    isRefreshing = false;
    // clearLocalStorage();
    // window.location.href = '/';
    return Promise.reject('loginAgain');
  }
};

// Helper function to create a delay
const delay = (seconds) => {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
};

// Function to disable a specific API
const disableApi = (url) => {
  disabledApis[url] = true;
  console.warn(
    `API calls to ${url} have been disabled due to repeated failures. Please try again later or contact support.`
  );
};

axiosInstance.interceptors.request.use(
  async (req) => {
    if (disabledApis[req.url]) {
      return Promise.reject(`API calls to ${req.url} are currently disabled`);
    }

    accessToken = getLocalStorage('accessToken', '') ? getLocalStorage('accessToken', '') : null;
    if (accessToken) {
      req.headers.Authorization = accessToken;

      let access = jwtDecode(accessToken);
      let isExpired = dayjs.unix(access.exp).diff(dayjs()) < 1;
      let isgetMethod = req?.method === 'get';
      let utmSource = getLocalStorage('utm_source', '');

      if (isgetMethod && utmSource) {
        let base_url = req.baseURL ? req.baseURL : baseURL;
        let req_url = new URL(base_url + req.url);

        if (!req_url.searchParams.has('utm_source')) {
          req_url.searchParams.append('utm_source', utmSource);
          req.url = req_url.toString();
        }
      }

      if (isExpired) {
        if (isRefreshing) {
          return addToQueue(req);
        }

        const updatedToken = await obtainNewAccessToken();
        req.headers['Authorization'] = updatedToken;
      }

      return req;
    }
  },
  async (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    try {
      var bytes = window
        .atob(response.data)
        .split('')
        .map(function (c) {
          return c.charCodeAt(0);
        });

      var inflated = pako.inflate(bytes, { to: 'string' });
      response.data = safeJsonParser(inflated, {});
      return response;
    } catch {
      return response;
    }
  },
  async (error) => {
    const originalRequest = error.config;

    if (typeof originalRequest === 'undefined' || typeof originalRequest?._retry === 'undefined') {
      originalRequest._retry = 0;
    }

    if (originalRequest._retry >= 3) {
      disableApi(originalRequest.url);
      return Promise.reject(
        `Maximum retry attempts reached. API calls to ${originalRequest.url} have been disabled.`
      );
    }

    if (error?.response?.status === 401) {
      if (isRefreshing) {
        return addToQueue(originalRequest);
      }

      if (originalRequest.url === `${authService}auth/getAccessToken`) {
        try {
          const response = await axios.post(`${authService}auth/getRefreshToken`, {
            token: getLocalStorage('refreshToken', '')
          });

          if (response.data.status_code === 200) {
            const newToken = response.data.data.token;
            setLocalStorage('accessToken', newToken);
            setLocalStorage('refreshToken', response.data.data.refresh_token);
            originalRequest.headers['Authorization'] = newToken;
            return axiosInstance(originalRequest);
          }

          throw new Error('Refresh token failed');
        } catch (refreshError) {
          disableApi(`${authService}auth/getAccessToken`);
          disableApi(`${authService}auth/getRefreshToken`);
          return Promise.reject(
            'Failed to refresh token. Token-related API calls have been disabled.'
          );
        }
      } else {
        try {
          const updatedToken = await obtainNewAccessToken();
          originalRequest.headers['Authorization'] = updatedToken;
          return axiosInstance(originalRequest);
        } catch (tokenError) {
          return Promise.reject('Failed to obtain new token. Please try again later.');
        }
      }
    } else {
      originalRequest._retry += 1;
      const retryDelay = originalRequest._retry;

      try {
        await delay(retryDelay);
        return axiosInstance(originalRequest);
      } catch (retryError) {
        if (originalRequest._retry >= 3) {
          disableApi(originalRequest.url);
          return Promise.reject(
            `All retry attempts failed. API calls to ${originalRequest.url} have been disabled.`
          );
        }
        return Promise.reject(retryError);
      }
    }
  }
);

export default axiosInstance;
